/**
 * The line under "CNH | Validade" that says what the typed date means.
 *
 * Advisory by design: it never blocks the submit. Letting someone drive in on an expired
 * licence is the condomínio's decision — the form's job is only to make sure nobody makes it
 * by accident, at the gate, doing date arithmetic in their head.
 */

import React from 'react';
import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { checkCnhValidade, CnhValidadeStatus } from '../../utils/cnhValidade';
import { cn } from '../../utils/cn';

/**
 * Which value the hint should judge where a field appears twice — a read-only line and, once
 * the operator clicks Editar, an input. Reading the saved value while typing would leave the
 * hint describing a date that is no longer on screen.
 */
export function pickCnhHintValue({ isEditing, draft, saved }: {
  isEditing: boolean;
  draft?: string | null;
  saved?: string | null;
}): string | null | undefined {
  return isEditing ? draft : saved;
}

interface CnhValidadeHintProps {
  value?: string | null;
  /** Injected in tests; production reads the clock. */
  today?: Date;
  darkMode?: boolean;
}

/**
 * Loudness follows consequence. A problem gets a filled block at body size — it has to survive
 * being glanced past on a form made of grey labels — while "válida" stays a caption: nobody
 * needs to be told twice that things are fine.
 */
const TONE: Record<CnhValidadeStatus, {
  Icon: typeof AlertTriangle;
  light: string;
  dark: string;
  loud: boolean;
}> = {
  vencida: {
    Icon: XCircle,
    light: 'bg-red-100 border-red-300 text-red-800',
    dark: 'bg-red-500/15 border-red-500/40 text-red-300',
    loud: true,
  },
  vence_em_breve: {
    Icon: AlertTriangle,
    light: 'bg-amber-100 border-amber-300 text-amber-900',
    dark: 'bg-amber-500/15 border-amber-500/40 text-amber-300',
    loud: true,
  },
  valida: {
    Icon: CheckCircle2,
    light: 'text-green-600',
    dark: 'text-green-400',
    loud: false,
  },
};

export function CnhValidadeHint({ value, today, darkMode = false }: CnhValidadeHintProps) {
  const check = checkCnhValidade(value, today);
  if (!check) return null;

  const { Icon, light, dark, loud } = TONE[check.status];

  return (
    <p
      data-testid="cnh-validade-hint"
      role="status"
      className={cn(
        'mt-1.5 flex items-center gap-1.5',
        loud
          ? 'text-sm font-bold rounded-lg border px-2.5 py-1.5'
          : 'text-xs font-medium',
        darkMode ? dark : light,
      )}
    >
      <Icon className={cn('flex-shrink-0', loud ? 'w-4 h-4' : 'w-3.5 h-3.5')} />
      {check.message}
    </p>
  );
}
